import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import { Page } from '@/components/layout/Page'
import { FullPageSpinner } from '@/components/common/Spinner'
import { Status } from '@/components/common/Status'
import { AlertCircleIcon } from '@/components/common/icons'
import { useSession } from '@/auth/useSession'

/**
 * Time-to-give-up on a silent OIDC code exchange. Past this the handshake
 * is almost certainly hung (bad state param, CSRF mismatch, clock skew,
 * proxy eating the POST to /token), and leaving the user staring at an
 * indefinite spinner is worse UX than surfacing an actionable error.
 */
const CALLBACK_TIMEOUT_MS = 10_000

/**
 * Post-OIDC-redirect landing.
 *
 * Happy path: `react-oidc-context` intercepts `?code=...&state=...` at the
 * AuthProvider layer, exchanges it for tokens, and flips `isAuthenticated`
 * to true. We then `navigate('/')` with replace so the code-bearing URL
 * never ends up in history.
 *
 * Error paths:
 *   - `auth.error` set by oidc-client-ts (bad state, revoked consent, PKCE
 *     mismatch, /token failure): render an actionable error screen.
 *   - 10s elapsed without isAuthenticated flipping: same screen — we treat
 *     it as a stuck handshake rather than wait indefinitely.
 * Both offer a "Try again" that kicks off a fresh signinRedirect.
 */
export function CallbackPage() {
  const navigate = useNavigate()
  const auth = useAuth()
  const { signin } = useSession()
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [auth.isLoading, auth.isAuthenticated, navigate])

  // Timeout only runs while the handshake is still pending. If we already
  // have an auth.error we don't need the timer; if we've already
  // authenticated we'll have navigated away.
  useEffect(() => {
    if (auth.error || auth.isAuthenticated) return
    const t = window.setTimeout(() => setTimedOut(true), CALLBACK_TIMEOUT_MS)
    return () => window.clearTimeout(t)
  }, [auth.error, auth.isAuthenticated])

  const failed = !!auth.error || timedOut

  if (failed) {
    return (
      <Page>
        <Status
          tone="bad"
          alert
          icon={<AlertCircleIcon />}
          title="Something went wrong signing you in."
          actions={
            <button
              type="button"
              className="btn"
              onClick={() => {
                void signin()
              }}
            >
              Try again
            </button>
          }
        >
          {auth.error
            ? 'Your sign-in couldn’t complete. This usually resolves with a fresh attempt.'
            : 'Your sign-in is taking longer than expected. Let’s try again.'}
        </Status>
      </Page>
    )
  }

  return (
    <Page>
      <FullPageSpinner label="Signing you in" />
    </Page>
  )
}
